import { Auth0Provider } from '@auth0/auth0-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { GoogleTagManager } from '@next/third-parties/google';
import { Session } from 'next-auth';
import App, { AppContext } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { Layout } from '@/common/components/Layout';
import { MyAppProps } from '@/common/components/Layout/types';
import { AppContextProvider } from '@/common/hooks/useAppContext';
import { decryptData } from '@/common/utils/AppConstants';
import { getAppCommonData } from '@/common/utils/getAppComonData';
import { sessionStorageManager } from '@/common/utils/sessionStorageManager';
import { createMsalInstance } from '@/lib/msal';

import '@/styles/globals.css';

const Head = dynamic(() => import('next/head'), { ssr: true });
const ServiceUnavailable = dynamic(() => import('@/pages/service-unavailable'), { ssr: false });
const FourOFour = dynamic(() => import('@/pages/404'), { ssr: false });
const MsalProviderWrapper = dynamic(() => import('@/common/components/MsalProviderWrapper'), { ssr: true });
const IntercomProvider = dynamic(() => import('@/common/utils/IntercomProvider'), { ssr: true });
const AnalyticEventsProvider = dynamic(() => import('@/common/components/AnalyticEvents/AnalyticEventsProvider'), { ssr: false });

type TProps = MyAppProps & { session: Session } & {
	appdata: string;
	layoutProps: {
		header: {
			data: {
				attributes: {
					unauthorizedUserRedirect: object;
					blockedUserRedirect: object;
				};
			};
		};
		subfooter: {
			data: object;
		};
		footer: {
			data: {
				attributes: {
					copyright: string;
				};
			};
		};
	};
	nonce: string;
};

const RootLayout = ({ Component, appdata, layoutProps, pageProps, nonce }: TProps) => {
	const router = useRouter();
	const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
	const [appContextData, setAppContextData] = useState<any>({});
	const [azureLoginRequired, setAzureLoginRequired] = useState<boolean>(false);

	const isBrowser = typeof window !== 'undefined';

	useEffect(() => {
		if (nonce && isBrowser) {
			window.nonce = nonce; // Set nonce in the window object for GTM use
		}
	}, [nonce, isBrowser])

	useEffect(() => {
		if (appdata) {
			const keyPart1 = appdata.substring(0, 128);
			const keyPart2 = appdata.slice(-128);
			const data = appdata.replace(keyPart1, '').replace(keyPart2, '');
			const key = keyPart1 + keyPart2;
			const decrypted = decryptData(data, key);
			if (decrypted) setAppContextData(JSON.parse(decrypted));
		}
	}, [appdata]);

	useEffect(() => {
		if (isBrowser) {
			window.addEventListener('storage', sessionStorageManager);

			localStorage.setItem('getSessionStorage', 'foobar');
			localStorage.removeItem('getSessionStorage');

			return () => {
				window.removeEventListener('storage', sessionStorageManager);
			};
		}
	}, [isBrowser]);

	const auth0Config = useMemo(() => {
		const { auth0_domain, auth0_clientId, auth0_audience_domain } = appContextData;
		if (auth0_domain && auth0_clientId) {
			return {
				domain: auth0_domain,
				clientId: auth0_clientId,
				redirectUri: isBrowser ? `${window.location.origin}/auth/callback` : '',
				audience: isBrowser ? `https://${auth0_audience_domain || auth0_domain}/api/v2/` : undefined,
				scope: 'openid profile email read:current_user offline_access',
			};
		}
	}, [appContextData, isBrowser]);

	const imageDomains = useMemo(() => {
		return appContextData?.image_domains || [];

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appContextData?.image_domains?.length])

	useEffect(() => {
		const initializeMsal = async () => {
			const { env_name, azure_ad_client_id, azure_ad_tenant_id, azure_ad_redirect_url } = appContextData;
			if (azure_ad_redirect_url && azure_ad_client_id && azure_ad_tenant_id) {
				try {
					const instance = await createMsalInstance(env_name, azure_ad_client_id, azure_ad_tenant_id, azure_ad_redirect_url, nonce);
					setMsalInstance(instance);
					setAzureLoginRequired(true);
				} catch (error) {
					console.error("Error creating MSAL instance:", error);
				}
			}
			else if ((!azure_ad_client_id && azure_ad_tenant_id) || (azure_ad_client_id && !azure_ad_tenant_id)) {
				console.error("Incomplete Azure AD configuration");
				setAzureLoginRequired(true);
			}
			else {
				setAzureLoginRequired(false);
			}
		};

		if (isBrowser && !msalInstance) initializeMsal();
	}, [isBrowser, msalInstance, appContextData]);

	const showDefaultHeader = pageProps?.pageProps?.data?.content?.[0]?.attributes?.showDefaultHeader || false;
	const showSubFooter = pageProps?.pageProps?.data?.content?.[0]?.attributes?.showSubfooter || false;
	const showDefaultFooter = false;

	const isApplicationForm = pageProps?.pageProps?.data?.content?.[0]?.attributes?.body?.[0]?.__component === 'page-body.application-cmp';
	const isError = pageProps?.errorCode || pageProps?.redirectSlug?.data?.attributes?.destination?.data?.attributes?.slug || pageProps?.redirectSlug?.data?.attributes?.destination?.data?.attributes?.externalUrl || '';

	return (
		<>
			<Head>
				{/* Preconnect to image domains */}
				{imageDomains.map((domain: string) => (
					<link key={domain} rel="preconnect" href={domain} />
				))}
			</Head>
			<MsalProviderWrapper msalInstance={msalInstance}>
				<GoogleTagManager nonce={nonce} gtmId={appContextData?.gtm_id} />
				{auth0Config && (
					<Auth0Provider
						domain={auth0Config.domain}
						clientId={auth0Config.clientId}
						authorizationParams={{
							redirect_uri: auth0Config.redirectUri,
							audience: auth0Config.audience,
							scope: auth0Config.scope,
							nonce: nonce,
						}}
						onRedirectCallback={(appState) => {
							router.push(appState?.returnTo || window.location.pathname);
							sessionStorage.removeItem('tempAuth');
						}}
						useRefreshTokens={true}
						cacheLocation='localstorage'
					>
						<AppContextProvider initialValues={appContextData}>
							<IntercomProvider appId={appContextData?.intercom_app_id} intercomEnableChat={appContextData?.intercom_enable_chat}>
								<>
									{pageProps?.serviceUnavailable?.toLowerCase() === 'true' ? (
										<ServiceUnavailable />
									) : pageProps?.errorCode ? (
										<FourOFour />
									) : (
										<AnalyticEventsProvider componentRef={{ layoutProps, pageProps }}>
											<Layout
												layoutType={['header', 'footer', 'subFooter'].includes(pageProps?.preview_type) ? 'Preview' : 'Main'}
												layoutProps={{
													...(['header', 'footer', 'subFooter'].includes(pageProps?.preview_type) ? null : { ...layoutProps }),
													showDefaultHeader,
													showSubFooter,
													showDefaultFooter,
													isError: isError,
													IALocation: pageProps?.pageProps?.data?.content?.[0]?.attributes?.IALocation,
													isApplicationForm,
													azureLoginRequired: azureLoginRequired,
													env_name: appContextData?.env_name,
													auth0Config,
													notifications: pageProps?.pageProps?.data?.content?.[0]?.attributes?.notifications,
													msalInstance: msalInstance,
													nonce
												}}
											>
												<main id='content-wrapper' className='w-full flex flex-col items-center'>
													<Component
														key={router.asPath}
														{...pageProps}
														subfooterProps={layoutProps?.subfooter}
														copyright={layoutProps?.footer?.data?.attributes?.copyright}
														unauthorizedUser Redirect={layoutProps?.header?.data?.attributes?.unauthorizedUserRedirect}
														blockedUserRedirect={layoutProps?.header?.data?.attributes?.blockedUserRedirect}
													/>
												</main>
											</Layout>
										</AnalyticEventsProvider>
									)}
								</>
							</IntercomProvider>
						</AppContextProvider>
					</Auth0Provider>
				)}
			</MsalProviderWrapper>
		</>
	);
};

RootLayout.getInitialProps = async (context: AppContext) => {
	const ctx = await App.getInitialProps(context);
	const nonce = context.ctx.req?.headers['x-nonce'] || '';
	const api_route = process.env.API_URL as string;
	try {
		const appCommonData = await getAppCommonData(api_route);

		return {
			...ctx,
			...appCommonData,
			nonce
		};
	} catch (error) {
		console.error("Error fetching initial props:", error);
		return {
			...ctx,
			appdata: null,
			layoutProps: {
				header: null,
				subfooter: null,
				footer: null,
			},
			nonce
		};
	}
};

export default RootLayout;